import mockLabelsData from "../data/mockData/labelsData";
import { putDemoTrackBlob, WL_DEMO_BLOB_PREFIX } from "./demoTrackBlobStore";

export const STORAGE_LABELS = "wl_bs_labels_extras";

function generateRandomHex(length) {
  return [...Array(length)]
    .map(() => Math.floor(Math.random() * 16).toString(16))
    .join("");
}

function readSessionLabelExtras() {
  if (typeof sessionStorage === "undefined") {
    return [];
  }
  try {
    const raw = sessionStorage.getItem(STORAGE_LABELS);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function mergeLabelsById(baseRows, extras) {
  const byId = new Map(baseRows.map(row => [String(row.id), row]));
  extras.forEach(row => {
    const id = String(row.id);
    byId.set(id, { ...byId.get(id), ...row });
  });
  return Array.from(byId.values());
}

function normalizeLabelImageField(value) {
  if (value == null || value === "") {
    return {};
  }
  if (typeof value === "object" && !Array.isArray(value)) {
    if (Object.keys(value).length === 0) {
      return {};
    }
    const src = value.full_size || value.thumb_small || value.thumb_medium || "";
    return {
      ...value,
      thumb_small: value.thumb_small || src,
      full_size: value.full_size || src,
    };
  }
  if (typeof value === "string") {
    return {
      thumb_small: value,
      full_size: value,
    };
  }
  return {};
}

/** Normalize logo / promo_graphic to the API object shape expected by list + edit screens. */
export function enrichLabelRow(row) {
  if (!row || typeof row !== "object") {
    return row;
  }
  const logo = normalizeLabelImageField(row.logo);
  const promo = normalizeLabelImageField(row.promo_graphic);
  return {
    ...row,
    logo: Object.keys(logo).length ? logo : row.logo,
    promo_graphic: Object.keys(promo).length ? promo : row.promo_graphic,
  };
}

/** Merged seed + session label rows. */
export function getLabelRows() {
  const merged = mergeLabelsById(mockLabelsData, readSessionLabelExtras());
  return merged
    .filter(row => row && row.id != null)
    .map(enrichLabelRow)
    .sort((a, b) => Number(a.id) - Number(b.id));
}

export function getLabelById(id) {
  if (id == null || id === "") {
    return null;
  }
  return getLabelRows().find(row => String(row.id) === String(id)) || null;
}

/** Demo substitute for POST labels/validate-label-name/ */
export function validateLabelName(labelName, excludeId = null) {
  const normalized = String(labelName || "").trim().toLowerCase();
  if (!normalized) {
    return {
      valid: false,
      status: "Label name is required.",
    };
  }

  const duplicate = getLabelRows().find(row => {
    if (excludeId != null && String(row.id) === String(excludeId)) {
      return false;
    }
    return String(row.name || "").trim().toLowerCase() === normalized;
  });

  if (duplicate) {
    return {
      valid: false,
      status:
        "Oops! This label already exists and is owned by another user. Please try another label name. If you legally own this label name and you own the name copyright, please contact our support team.",
    };
  }

  return {
    valid: true,
    status: "Label name is available",
  };
}

/** Store label image in IndexedDB and return a wl-demo blob ref (no S3). */
export async function labelImageUpload(file, kind = "logo") {
  if (!file) {
    return null;
  }
  const fileNameBase = file?.name || kind;
  const fileType = fileNameBase.includes(".")
    ? fileNameBase.split(".").pop()
    : "png";
  const fileName = generateRandomHex(10);
  const storageKey = `label-${kind}:${fileName}.${fileType}`;
  try {
    await putDemoTrackBlob(storageKey, file);
  } catch (e) {
    console.warn("[labelImageUpload] IndexedDB store failed", e);
  }
  return `${WL_DEMO_BLOB_PREFIX}${encodeURIComponent(storageKey)}`;
}
